import { Centrifuge, UnauthorizedError } from 'centrifuge'

import { EMPTY_OBJECT, EMPTY_STRING } from '../../constants/common'
import { CENTRIFUGE_DOMAIN } from '../../env'
import { getOrigin } from '../../utils/api'
import { isDevelopmentEnvironment } from '../../utils/common'

import { getRealtimeConnectToken } from './Centrifuge.api'
import {
  CENTRIFUGE_LOG_PREFIX,
  CENTRIFUGO_WS_PATH,
} from './Centrifuge.constants'

/*
 * Dev-only logger. Production keeps the noise off the console; real failures
 * still go through `console.error` at the call sites.
 */
export function logCentrifuge(...args) {
  if (isDevelopmentEnvironment()) {
    console.log(CENTRIFUGE_LOG_PREFIX, ...args)
  }
}

export function consoleWarnCentrifuge(...args) {
  if (isDevelopmentEnvironment()) {
    console.warn(CENTRIFUGE_LOG_PREFIX, ...args)
  }
}

/*
 * Resolve the Centrifugo WebSocket URL.
 *
 * - Production: explicit `CENTRIFUGE_DOMAIN` env wins.
 * - Development: derive from `getOrigin()` and route through the dev proxy
 *   path (`/centrifugo/connection/websocket`). A protocol-relative origin
 *   follows the page protocol, so the http `start-local` server gets `ws://`.
 *
 * Returns `null` when the origin cannot be normalised into a `ws(s)://` URL —
 * callers must treat that as "realtime is unavailable" and degrade gracefully.
 */
export function getCentrifugoWebSocketUrl() {
  if (CENTRIFUGE_DOMAIN) {
    return `wss://${CENTRIFUGE_DOMAIN}${CENTRIFUGO_WS_PATH}`
  }

  const origin = getOrigin()

  if (!origin) {
    return null
  }

  const pageWsProtocol =
    window.location.protocol === 'https:' ? 'wss://' : 'ws://'

  const wssOrigin = origin
    .replace(/^https:\/\//, 'wss://')
    .replace(/^http:\/\//, 'ws://')
    .replace(/^\/\//, pageWsProtocol)
    .replace(/\/$/, EMPTY_STRING)

  if (!/^wss?:\/\//.test(wssOrigin)) {
    console.warn(
      `${CENTRIFUGE_LOG_PREFIX} cannot derive WebSocket URL from origin "${origin}"`
    )

    return null
  }

  return `${wssOrigin}${CENTRIFUGO_WS_PATH}`
}

/*
 * `getToken` callback for the Centrifuge client.
 *
 * Fetches a fresh short-lived JWT from `/idwell-api/realtime/connect/`. Throws
 * `UnauthorizedError` when the response carries no token so the SDK stops
 * retrying and moves the client into the disconnected state.
 *
 * Note: real 401s never land here — `src/utils/api.js` handles them globally
 * by redirecting to `/login`. The only `UnauthorizedError` path is a 200 with
 * an empty token in the response body.
 */
export async function fetchCentrifugoToken({ outboundUuid } = EMPTY_OBJECT) {
  try {
    const response = await getRealtimeConnectToken({ outboundUuid })
    const token = response?.data?.token

    if (!token) {
      throw new UnauthorizedError('No token in /realtime/connect/ response')
    }

    logCentrifuge(`token fetched (ttl=${response?.data?.ttl})`)

    return token
  } catch (error) {
    console.error(`${CENTRIFUGE_LOG_PREFIX} token fetch failed`, error)
    throw error
  }
}

/*
 * Build a Centrifuge client wired to the resolved WebSocket URL and the
 * project's token endpoint. Returns `null` when no WebSocket URL can be
 * resolved or the SDK constructor throws — callers should treat that as
 * "realtime is unavailable".
 */
export function createCentrifugeClient({ outboundUuid } = EMPTY_OBJECT) {
  const wsUrl = getCentrifugoWebSocketUrl()

  if (!wsUrl) {
    console.warn(`${CENTRIFUGE_LOG_PREFIX} no WebSocket URL available`)

    return null
  }

  try {
    return new Centrifuge(wsUrl, {
      getToken: () => fetchCentrifugoToken({ outboundUuid }),
    })
  } catch (error) {
    console.warn(
      `${CENTRIFUGE_LOG_PREFIX} failed to create client for "${wsUrl}"`,
      error
    )

    return null
  }
}

/*
 * Defensive `disconnect()` — the SDK can throw if the client is already in a
 * terminal state. Used by the provider on teardown and by the connection
 * release path.
 */
export function safeDisconnectClient(client) {
  try {
    client.disconnect()
  } catch (error) {
    console.warn(`${CENTRIFUGE_LOG_PREFIX} client cleanup error`, error)
  }
}

export function isRealtimeEvent(ctx, event) {
  return ctx?.data?.event === event
}
